"use server";

import crypto from "node:crypto";
import { db } from "@/lib/db";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit";
import { sendPasswordResetEmail } from "@/lib/email";
import { verifyTurnstile } from "@/lib/turnstile";
import { SITE_URL } from "@/lib/site";

const RESET_TOKEN_TTL_MS = 60 * 60 * 1000;

const GENERIC_SUCCESS =
  "Jeśli konto z tym adresem email istnieje, wysłaliśmy na nie link do zresetowania hasła.";

function hashToken(token: string): string {
  return crypto.createHash("sha256").update(token).digest("hex");
}

export type ForgotPasswordState = { error?: string; success?: string } | undefined;

export async function requestPasswordReset(
  _prevState: ForgotPasswordState,
  formData: FormData,
): Promise<ForgotPasswordState> {
  const ip = await getClientIp();
  if (!checkRateLimit(`forgot-password:${ip}`, 5, 60 * 60 * 1000)) {
    return { error: "Zbyt wiele prób. Spróbuj ponownie później." };
  }

  if (!(await verifyTurnstile(formData, ip))) {
    return { error: "Nie udało się zweryfikować, że nie jesteś botem. Odśwież stronę i spróbuj ponownie." };
  }

  const email = formData.get("email");
  if (typeof email !== "string" || !email) {
    return { error: "Podaj adres email." };
  }

  // Rate-limit per email too, so a spoofed/rotating IP can't be used to spam
  // one inbox with reset links.
  if (!checkRateLimit(`forgot-password:${email}`, 5, 60 * 60 * 1000)) {
    return { error: "Zbyt wiele prób. Spróbuj ponownie później." };
  }

  const user = await db.user.findUnique({ where: { email } });

  // Always return the same message whether or not the account exists -
  // otherwise this becomes an email-enumeration oracle.
  if (user) {
    const token = crypto.randomBytes(32).toString("hex");

    await db.passwordResetToken.deleteMany({ where: { userId: user.id } });
    await db.passwordResetToken.create({
      data: {
        userId: user.id,
        tokenHash: hashToken(token),
        expiresAt: new Date(Date.now() + RESET_TOKEN_TTL_MS),
      },
    });

    const resetUrl = `${SITE_URL}/reset-password?token=${token}`;
    await sendPasswordResetEmail(user.email, resetUrl);
  }

  return { success: GENERIC_SUCCESS };
}
